import React, { useCallback } from 'react';
import { useDropzone, FileRejection, DropzoneOptions } from 'react-dropzone';
import { LoadingSpinner } from '@/shared/components';

/**
 * Интерфейс пропсов для компонента FileUploadDropzone
 */
interface FileUploadDropzoneProps {
  /** Функция обратного вызова при выборе файлов */
  onFileSelect: (files: File[]) => void;
  /**
   * Отклонённые файлы — не тот тип или больше maxSize. Без обработчика они
   * пропадают молча (только запись в консоль).
   */
  onFilesRejected?: (rejections: FileRejection[]) => void;
  /** Принимаемые типы файлов (по умолчанию изображения) */
  accept?: string;
  /** Разрешить выбор нескольких файлов */
  multiple?: boolean;
  /** Максимальный размер файла в байтах */
  maxSize?: number;
  /** Состояние загрузки */
  disabled?: boolean;
  /** Дополнительные CSS классы */
  className?: string;
  /** Текст для отображения в зоне перетаскивания */
  dropzoneText?: string;
  /** Текст с описанием ограничений */
  helpText?: string;
}

/**
 * Компонент зоны загрузки файлов с поддержкой drag-and-drop
 * Использует react-dropzone для реализации функциональности перетаскивания
 * 
 * @param onFileSelect - Функция вызываемая при выборе файлов
 * @param onFilesRejected - Функция вызываемая для отклонённых файлов
 * @param accept - Принимаемые типы файлов
 * @param multiple - Разрешить множественный выбор
 * @param maxSize - Максимальный размер файла
 * @param disabled - Состояние отключения
 * @param className - Дополнительные CSS классы
 * @param dropzoneText - Текст в зоне перетаскивания
 * @param helpText - Текст с описанием ограничений
 * @returns JSX элемент
 */
export const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  onFileSelect,
  onFilesRejected,
  accept = 'image/jpeg,image/jpg,image/png,image/webp',
  multiple = false,
  maxSize = 5 * 1024 * 1024, // 5MB по умолчанию
  disabled = false,
  className = '',
  dropzoneText = 'Перетащите файлы сюда или нажмите для выбора',
  helpText = 'Разрешены файлы JPG, PNG, WebP. Максимальный размер: 5MB'
}) => {
  /**
   * Обработчик успешного выбора файлов
   */
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles);
      }
    },
    [onFileSelect]
  );

  /**
   * Обработчик отклоненных файлов
   */
  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      console.warn('Файлы отклонены:', fileRejections);
      onFilesRejected?.(fileRejections);
    },
    [onFilesRejected]
  );

  /**
   * Конфигурация dropzone
   */
  const dropzoneOptions: DropzoneOptions = {
    onDrop,
    onDropRejected,
    accept: {
      [accept.split(',')[0] ?? 'image/*']: accept.split(',')
    },
    multiple,
    maxSize,
    disabled,
    noClick: disabled,
    noKeyboard: disabled
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    isFocused
  } = useDropzone(dropzoneOptions);

  /**
   * Определение CSS классов для состояний dropzone
   */
  const getDropzoneClasses = (): string => {
    const baseClasses = [
      'border-2',
      'border-dashed',
      'rounded-lg',
      'p-6',
      'text-center',
      'transition-all',
      'duration-200',
      'cursor-pointer',
      'min-h-32',
      'flex',
      'flex-col',
      'items-center',
      'justify-center',
      'gap-3'
    ];

    if (disabled) {
      baseClasses.push(
        'border-base-300',
        'bg-base-200',
        'text-base-content/40',
        'cursor-not-allowed'
      );
    } else if (isDragReject) {
      baseClasses.push(
        'border-error',
        'bg-error/10',
        'text-error'
      );
    } else if (isDragAccept || isDragActive) {
      baseClasses.push(
        'border-primary',
        'bg-primary/10',
        'text-primary'
      );
    } else if (isFocused) {
      baseClasses.push(
        'border-primary/50',
        'bg-base-100',
        'text-base-content'
      );
    } else {
      baseClasses.push(
        'border-base-300',
        'bg-base-100',
        'text-base-content',
        'hover:border-primary/50',
        'hover:bg-primary/5'
      );
    }

    return [...baseClasses, className].join(' ');
  };

  /**
   * Получение иконки для текущего состояния
   */
  const getIcon = (): string => {
    if (disabled) {
      return 'fas fa-ban';
    } else if (isDragReject) {
      return 'fas fa-times-circle';
    } else if (isDragAccept || isDragActive) {
      return 'fas fa-check-circle';
    } else {
      return 'fas fa-cloud-upload-alt';
    }
  };

  /**
   * Получение текста для текущего состояния
   */
  const getDropzoneDisplayText = (): string => {
    if (disabled) {
      return 'Загрузка отключена';
    } else if (isDragReject) {
      return 'Неподдерживаемый тип файла';
    } else if (isDragActive) {
      return 'Отпустите файлы для загрузки';
    } else {
      return dropzoneText;
    }
  };

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={getDropzoneClasses()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Зона загрузки файлов"
        aria-describedby="dropzone-help"
      >
        <input {...getInputProps()} aria-hidden="true" />
        
        {disabled ? (
          <LoadingSpinner size="sm" />
        ) : (
          <i className={`${getIcon()} text-2xl`} aria-hidden="true"></i>
        )}
        
        <div className="text-sm font-medium">
          {getDropzoneDisplayText()}
        </div>
        
        {!disabled && (
          <div className="text-xs opacity-60">
            {multiple ? 'Можно выбрать несколько файлов' : 'Выберите один файл'}
          </div>
        )}
      </div>
      
      {helpText && (
        <p 
          id="dropzone-help" 
          className="text-xs text-base-content/60 mt-2"
        >
          {helpText}
        </p>
      )}
    </div>
  );
};
